import React, { useRef, useState } from 'react';
import { Bookmark, Pencil, X } from 'lucide-react';
import apiClient from '../../config/axios';
import confirmDialog from '../ConfirmDialog';

/**
 * Saved searches (issue 186): name the current filter state of a list page,
 * re-apply it in one click.
 *
 * FILTER STATE LIVES ENTIRELY IN THE URL (nav contract point 1), so this
 * component never touches a filter control: SAVE captures the page's query
 * string (minus the params below) and APPLY hands a stored query string back
 * to the page, which writes it with `setSearchParams` — exactly what loading a
 * shared link does. The URL → state resync effects do the rest, so a saved
 * search made before a filter existed, or naming one since removed, degrades
 * exactly as a stale bookmarked link does: unknown params are ignored, a
 * dangling value shows an explained empty list with its chip clearable.
 *
 * Rendered by GenericList for signed-in readers only — every Family gets it,
 * including one authored this afternoon, because nothing here names an entity.
 *
 * NOT a <form>: this sits inside the list page's search <form>, and nesting is
 * invalid HTML — Enter in the name input is handled directly so it saves
 * rather than submitting the page search.
 */

// The params a saved search never carries. Mirrors STRIPPED_PARAMS in
// server/routes/saved-searches.js, which strips them again server-side —
// `page` because a saved search that always lands on page 4 is a bug, the
// rest because they address other views (`slug`, `compare`) or scoping axes
// the row and the session already carry (`system`, `stack`).
export const STRIPPED_PARAMS = ['page', 'slug', 'compare', 'system', 'stack'];

/** The query string a save captures: the current URL params minus the above. */
export function captureQuery(searchParams) {
  const params = new URLSearchParams(searchParams);
  for (const key of STRIPPED_PARAMS) params.delete(key);
  return params.toString();
}

const byName = (a, b) => a.name.localeCompare(b.name);

export default function SavedSearches({ segment, currentQuery, onApply }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState(null);
  const [renaming, setRenaming] = useState(null); // { id, value }
  const loadingRef = useRef(false);

  // Lazy: fetched on first open, not per list view.
  const load = async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    try {
      const res = await apiClient.get(`/api/saved-searches?entity_type=${segment}`);
      setItems(Array.isArray(res.data) ? res.data : []);
      setLoaded(true);
      setError(null);
    } catch (err) {
      console.error('Error fetching saved searches:', err);
      setError('Could not load saved searches');
    } finally {
      loadingRef.current = false;
    }
  };

  const toggle = () => {
    setOpen((v) => !v);
    if (!loaded) load();
  };

  const saveCurrent = async () => {
    const trimmed = name.trim();
    if (!trimmed || !currentQuery) return;
    try {
      const res = await apiClient.post('/api/saved-searches', {
        entity_type: segment,
        name: trimmed,
        query_string: currentQuery,
      });
      // 200 = replaced an existing name, 201 = created; either way the row
      // returned is current.
      setItems((prev) => [...prev.filter((i) => i.id !== res.data.id), res.data].sort(byName));
      setName('');
      setError(null);
    } catch (err) {
      console.error('Error saving search:', err);
      setError(err.response?.data?.error === 'too_many_saved_searches'
        ? 'Saved search limit reached'
        : 'Could not save');
    }
  };

  const applyItem = (item) => {
    onApply(item.query_string);
    setOpen(false);
  };

  const commitRename = async () => {
    if (!renaming) return;
    const trimmed = renaming.value.trim();
    const item = items.find((i) => i.id === renaming.id);
    if (!trimmed || !item || trimmed === item.name) {
      setRenaming(null);
      return;
    }
    try {
      const res = await apiClient.put(`/api/saved-searches/${renaming.id}`, { name: trimmed });
      setItems((prev) => prev.map((i) => (i.id === res.data.id ? res.data : i)).sort(byName));
      setRenaming(null);
      setError(null);
    } catch (err) {
      console.error('Error renaming saved search:', err);
      setError(err.response?.status === 409 ? 'That name is already taken' : 'Could not rename');
    }
  };

  // A shared saved search asks first: deleting it kills the link (issue 1268).
  // The sharing status is asked on the click, never per list view.
  const remove = async (item) => {
    let shared = false;
    try {
      const res = await apiClient.get(`/api/sharing/saved-searches/${item.id}`);
      shared = !!(res.data && res.data.link);
    } catch (err) { shared = false; }
    if (shared && !(await confirmDialog(`Delete ${item.name}? Its share link stops working.`, { confirmLabel: 'Delete' }))) return;
    try {
      await apiClient.delete(`/api/saved-searches/${item.id}`);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      setError(null);
    } catch (err) {
      console.error('Error deleting saved search:', err);
      setError('Could not delete');
    }
  };

  // Enter must save, not submit the page's search form this sits inside.
  const onNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveCurrent();
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={toggle}
        className="filter-checkbox-label"
        aria-expanded={open}
        aria-haspopup="true"
      >
        <Bookmark className="w-4 h-4" style={{ color: '#C9A84C' }} aria-hidden="true" />
        <span className="filter-checkbox-text">Saved searches</span>
      </button>

      {open && (
        <div
          className="card absolute left-0 top-full mt-2 z-30 w-72 max-w-[85vw] space-y-3 shadow-lg"
          data-testid="saved-searches-menu"
        >
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={onNameKeyDown}
              maxLength={80}
              placeholder="Name current filters…"
              aria-label="Saved search name"
              className="input flex-1 min-w-0"
            />
            <button
              type="button"
              onClick={saveCurrent}
              disabled={!name.trim() || !currentQuery}
              title={currentQuery ? 'Save the current filters' : 'Set some filters first'}
              className="btn btn-primary"
            >
              Save
            </button>
          </div>

          {error && <p className="error-message text-sm" role="alert">{error}</p>}

          {loaded && items.length === 0 && !error && (
            <p className="text-secondary text-sm">No saved searches yet.</p>
          )}
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
            <a href="/account?tab=lists" className="underline">Manage and share your saved lists</a> on your profile.
          </p>

          {items.length > 0 && (
            <ul className="space-y-1" aria-label="Saved searches">
              {items.map((item) => (
                <li key={item.id} className="flex items-center gap-1">
                  {renaming && renaming.id === item.id ? (
                    <input
                      type="text"
                      value={renaming.value}
                      autoFocus
                      maxLength={80}
                      aria-label={`New name for ${item.name}`}
                      onChange={(e) => setRenaming({ id: item.id, value: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); commitRename(); }
                        if (e.key === 'Escape') setRenaming(null);
                      }}
                      onBlur={commitRename}
                      className="input flex-1 min-w-0"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => applyItem(item)}
                      className="flex-1 min-w-0 text-left text-sm py-1 px-2 rounded hover:bg-black/10 dark:hover:bg-white/10 truncate"
                      aria-label={`Apply ${item.name}`}
                      title={`Apply "${item.name}"`}
                    >
                      {item.name}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setRenaming({ id: item.id, value: item.name })}
                    aria-label={`Rename ${item.name}`}
                    title="Rename"
                    className="p-1 text-secondary hover:text-primary"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(item)}
                    aria-label={`Delete ${item.name}`}
                    title="Delete"
                    className="p-1 text-secondary hover:text-primary"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
